import { useAssetVizInternal } from '../AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  PositionCalculator,
  SelectionCallback,
} from '../cameras/DefaultCamera'
import { LaunchTubeSurface3D } from '../objects/surfaces/LaunchTubeSurface3D'
import { SceneFactoryConfig } from './sceneFactory'
import React from 'react'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoEditableComponent,
  GeckoLaunchTube,
  GeckoMeasurementUnit,
} from 'types'

export const MissileSiloSceneConfig: SceneFactoryConfig = {
  name: 'MissileSilo',
  assetModels: [GeckoAssetModelType.MissileSilo],
  componentModels: [GeckoComponentModelType.LaunchTube],
  sceneFunc: (props) => <MissileSiloScene {...props} />,
}

/**
 * A scene depicting a Missile Silo which wraps around the y-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 * @constructor
 */
export const MissileSiloScene: React.FC<{
  assetModel: GeckoAssetModel
  componentModel: GeckoEditableComponent
}> = ({ assetModel, componentModel }) => {
  const selectedModel = componentModel as GeckoLaunchTube

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    const componentBounds = selectedModel.componentBounds
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Meters ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Launch Tubes in the asset model should always be in meters, but 
              the x range is in ${componentBounds.x.unit.id}
              and y range is in ${componentBounds.y.unit.id}`
      )
    return componentBounds
  }, [selectedModel.componentBounds])

  const tankRadius = React.useMemo(() => selectedModel.radius, [selectedModel])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentDataFrames, componentBounds]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
      doWrapX: show3D,
    }),
    [componentBounds, show3D]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num
      if (!show3D)
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0],
        }
      // ...otherwise we're looking at the shell in 3D mode
      const angle =
        2.0 * Math.PI * (relX / (tankRadius.num * 2 * Math.PI) - 0.25)

      const distToAxis = tankRadius.num + distToComponent
      return {
        position: [
          Math.sin(angle) * distToAxis,
          relY,
          Math.cos(angle) * distToAxis,
        ],
        rotation: [0, angle, 0],
      }
    },
    [componentBounds, tankRadius.num, show3D]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) =>
      show3D
        ? [Math.min(rect.width.num, tankRadius.num * 2.0), rect.height.num]
        : [rect.width.num, rect.height.num],
    [show3D, tankRadius]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <group>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoLaunchTube ? (
        <LaunchTubeSurface3D model={selectedModel} assetModel={assetModel} />
      ) : null}
    </group>
  )
}
